import React, { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useApp } from "../context/AppContext";
import { META, getBySection, getStats, recordAnswer, searchQuestions } from "../lib/store";
import { ChevronLeft, ChevronRight, FolderOpen, Search } from "lucide-react";
import { InteractiveQuestion } from "../components/InteractiveQuestion";

const PAGE = 15;

function SectionGrid() {
  const { level } = useApp();
  const cfg = META[level];
  const stats = useMemo(() => getStats(level), [level]); // eslint-disable-line react-hooks/exhaustive-deps
  const secStats = Object.fromEntries(stats.sections.map((s) => [s.code, s]));
  const [query, setQuery] = useState("");
  const results = useMemo(() => searchQuestions(level, query), [level, query]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-8">
      <div>
        <div className="font-mono text-xs uppercase tracking-[0.2em] text-primary">
          Qualification {cfg.label}
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight mt-2">Étudier</h1>
        <p className="text-muted-foreground mt-2 text-sm">
          {cfg.total} questions officielles ISED réparties en {cfg.sections.length} sections.
        </p>
      </div>

      <div className="relative max-w-xl">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          data-testid="study-search"
          placeholder="Rechercher dans toutes les questions (ex. antenne, ionosphère…)"
          className="w-full rounded-sm border border-border bg-card pl-9 pr-3 py-2.5 text-sm outline-none focus-visible:ring-2 focus-visible:ring-primary"
        />
      </div>

      {query.trim() ? (
        <div className="space-y-3" data-testid="study-search-results">
          <div className="text-sm text-muted-foreground">
            {results.length} résultat{results.length > 1 ? "s" : ""}{results.length >= 60 ? " (60 premiers)" : ""} pour « {query} »
          </div>
          {results.map((q, i) => (
            <InteractiveQuestion
              key={q.id}
              q={q}
              index={i}
              onAnswered={(ok, qq) => recordAnswer(level, qq.id, ok)}
              testPrefix="study-search-q"
            />
          ))}
          {results.length === 0 && (
            <div className="text-center text-sm text-muted-foreground py-10">Aucune question trouvée.</div>
          )}
        </div>
      ) : (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {cfg.sections.map((s, i) => {
          const st = secStats[s.code] || { mastered: 0, total: s.count };
          const pct = st.total ? Math.round((st.mastered / st.total) * 100) : 0;
          return (
            <motion.div
              key={s.code}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.04 }}
            >
              <Link
                to={`/etudier/${s.code}`}
                data-testid={`section-${s.code}`}
                className="card-brutal block rounded-sm border border-border bg-card p-5 h-full transition-[box-shadow,transform]"
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs text-primary font-semibold">{s.code}</span>
                  <span className="font-mono text-xs text-muted-foreground">{s.count} Q</span>
                </div>
                <h3 className="font-display font-bold text-lg mt-3 leading-tight">{s.name}</h3>
                <div className="mt-4">
                  <div className="h-2 rounded-sm bg-muted overflow-hidden">
                    <div
                      className="h-full rounded-sm transition-[width]"
                      style={{ width: `${pct}%`, background: pct >= 70 ? "hsl(var(--success))" : "hsl(var(--warning))" }}
                    />
                  </div>
                  <div className="flex justify-between mt-1.5 text-[11px] font-mono text-muted-foreground">
                    <span>{st.mastered} maîtrisées</span>
                    <span>{pct}%</span>
                  </div>
                </div>
              </Link>
            </motion.div>
          );
        })}
      </div>
      )}
    </div>
  );
}

function SectionDetail({ code }) {
  const { level } = useApp();
  const sec = META[level].sections.find((s) => s.code === code);
  const questions = useMemo(() => getBySection(level, code), [level, code]); // eslint-disable-line react-hooks/exhaustive-deps
  const [page, setPage] = useState(0);
  const pages = Math.ceil(questions.length / PAGE);
  const slice = questions.slice(page * PAGE, page * PAGE + PAGE);

  return (
    <div className="space-y-6">
      <Link to="/etudier" data-testid="back-to-sections" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors">
        <ChevronLeft size={16} /> Toutes les sections
      </Link>
      <div className="flex items-center gap-3">
        <span className="grid place-items-center w-11 h-11 rounded-sm bg-primary/10 text-primary shrink-0">
          <FolderOpen size={22} />
        </span>
        <div>
          <div className="font-mono text-xs text-primary">{sec?.code}</div>
          <h1 className="font-display font-black text-2xl sm:text-3xl tracking-tight">{sec?.name}</h1>
        </div>
      </div>

      <p className="text-sm text-muted-foreground">
        Répondez à chaque question puis validez : la correction apparaît aussitôt.
      </p>

      <div className="space-y-3">
        {slice.map((q, idx) => (
          <InteractiveQuestion
            key={q.id}
            q={q}
            index={page * PAGE + idx}
            onAnswered={(ok, qq) => recordAnswer(level, qq.id, ok)}
            testPrefix="study-q"
          />
        ))}
      </div>

      {pages > 1 && (
        <div className="flex items-center justify-between">
          <button
            disabled={page === 0}
            onClick={() => setPage((p) => p - 1)}
            data-testid="study-prev"
            className="inline-flex items-center gap-1 rounded-sm border border-border px-4 py-2 text-sm disabled:opacity-40 hover:bg-secondary transition-colors"
          >
            <ChevronLeft size={16} /> Précédent
          </button>
          <span className="font-mono text-sm text-muted-foreground">
            {page + 1} / {pages}
          </span>
          <button
            disabled={page === pages - 1}
            onClick={() => setPage((p) => p + 1)}
            data-testid="study-next"
            className="inline-flex items-center gap-1 rounded-sm border border-border px-4 py-2 text-sm disabled:opacity-40 hover:bg-secondary transition-colors"
          >
            Suivant <ChevronRight size={16} />
          </button>
        </div>
      )}
    </div>
  );
}

export default function Study() {
  const { code } = useParams();
  return code ? <SectionDetail code={code} /> : <SectionGrid />;
}
